
import React, { useState, useEffect, useCallback, useRef } from 'react';

import slugify from 'react-slugify';
import Backend from '../../../utils/Backend';
import { toast } from 'react-toastify'
import { Canceler } from 'axios';
import { formatPrice } from '../../../helper/vehicle_formatter';

import EVModal from '../EVModal';
import EVSelector from '../EVSelector';
import Loader from '../../Loader';

import IOption from '../../../interfaces/IOption';
import IModalState from '../../../interfaces/IModalState';

import styles from './styles.module.scss'
import useCancel from '../../Hooks/CancelFetch';
import { useIsBoiWebsite } from "../../Context/WebsiteType";

interface IEVsSelector {
  setVehicles(vehicle: any[]): void,
  mobile: boolean | undefined,
  processURL: undefined | boolean,
  setProcessURL: (processURL: undefined | boolean) => void,
  query: any
}

const base_state = {
  make: null,
  model: null,
  models: [],
  variant: null,
  variants: [],
}

export default function EVsSelector({ setVehicles, mobile, processURL, setProcessURL, query }: IEVsSelector) {
  const [makes, setMakes] = useState<IOption[]>([]);
  
  const [modalIndex, setModalIndex] = useState<number>(0)
  const [modalState, setModalState] = useState<IModalState>()
  const [modalStates, setModalStates] = useState<IModalState[]>([{...base_state}])
  const [updateStates, setUpdateStates] = useState(false)
  const [updateVehicles, setUpdateVehicles] = useState(false)

  const [scrollY, setScrollY] = useState(0);

  const modelsCancelRef = useRef<Canceler>();
  const variantsCancelRef = useRef<Canceler>();

  const vehiclesLimit = mobile ? 2 : 4;

  const isBoiWebsite = useIsBoiWebsite();

  // on resizing window, if it shrinks to mobile it should leave only two cars
  // if it grows to tablet/desktop it should add empty card for new car if 2 selected
  useEffect(() => {
    if (mobile && modalStates.length > 2) {
      setModalStates(modalStates.slice(0, 2))
    }
    else if (mobile === false && modalStates.length === 2 && !hasEmpty(modalStates)) {
      setModalStates([...modalStates, {...base_state}]);
    }
  }, [mobile, modalStates]);

  // fetch all makes
  useEffect(() => {
    const { promise, cancel } = Backend.getAllMakes()
    
    promise.then(({data}) => {
      if (!data.content) {
        toast.error("Failed to fetch Makes")
      }

      const makes = []
      for (const make of data.content) {
        makes.push({ 
          label: make.name,
          value: make.id 
        })
      }      

      setMakes(makes)
    })

    return cancel;
  }, []);

  
  useCancel(modelsCancelRef.current)
  useCancel(variantsCancelRef.current)
  const processVehicle = useCallback(async (vehicle: string[], stateIndex: number) => {
    if (vehicle.length !== 3) return false; // invalid url
    
    if (!modalStates[stateIndex]) { // creating empty state
      modalStates.push({...base_state}) 
    }

    let state = modalStates[stateIndex];

    const [make, model, variant_id] = vehicle;

    // finding make
    const makeOption = makes.find(option => slugify(option.label) === make)
    if (!makeOption) return false // invalid make
      
    // fetching models
    const { promise: modelPromise, cancel: modelCancel } = Backend.getVehicles([makeOption], undefined, true)
    modelsCancelRef.current = modelCancel
    const { data: modelData } = await modelPromise;

    if (!modelData.content || Object.keys(modelData?.content?.vehicles).length === 0) {
      return false; // invalid model
    }
    const models = modelData.content.vehicles;

    // finding model
    const modelOption = models.find((option: any) => slugify(option.label) === model)
    if (!modelOption) return false // invalid model

    // fetching variants
    const { 
      promise: variantPromise, cancel: variantCancel 
    } = Backend.getOptions(parseInt(`${makeOption.value}`), parseInt(`${modelOption.value}`))
    variantsCancelRef.current = variantCancel
    const { data: variantData } = await variantPromise;
  
    if (!variantData?.content?.vehicles || Object.keys(variantData.content.vehicles).length === 0) {
      return false; // invalid variant
    }

    const variants = variantData.content.vehicles.map((variant: any) => {
      return {
        value: variant,
        label: `${variant.trim_level} - ${formatPrice(variant.price)}`,
      }
    });

    // finding variant
    const variantOption = variants.find((option: any) => option.value.third_party.variantId === parseInt(variant_id))
    if (!variantOption) return false // invalid variant

    // Updating state
    state.make = makeOption;
    state.model = modelOption;
    state.models = models;
    state.variant = variantOption;
    state.variants = variants;
    
    modalStates[stateIndex] = state;
    setModalStates(modalStates);

    return true;
  }, [makes, modalStates])


  // process URL (query)
  useEffect(() => {
    if (mobile === undefined) return;

    if (processURL && query && makes.length > 0) {
      const asyncProcess = async () => {
        let count = 0;
        
        for (const [key, value] of Object.entries(query)) {
          if (count >= vehiclesLimit) break; // for mobile process only 2 vehicles

          const vehicleQuery: any = value;
          const vehicleData = vehicleQuery.split('/'); // make/model/variant_id
          
          const valid = await processVehicle(vehicleData, count);

          if (!valid) { // broken URL
            setModalStates([{...base_state}]) // resetting states
            setProcessURL(false);
            return;
          }

          ++count;
        }

        setUpdateStates(true); // Successfully proccessed URL!
        setProcessURL(false);
      }

      asyncProcess();
    }
  }, [vehiclesLimit, makes, processURL, setProcessURL, query, processVehicle, mobile])


  // pass all vehicles to parent component
  useEffect(() => {
    if (updateVehicles) {
      setUpdateVehicles(false)

      const vehicles: any[] = []

      modalStates.forEach(({ variant }) => {
        if (variant) {
          vehicles.push(variant)
        }
      })
  
      setVehicles(vehicles)
    }
  }, [updateVehicles, modalStates, setVehicles])


  const openModal = (modalState: IModalState, index: number) => {
    const htmlDoc = document.children[0] as HTMLElement;
    htmlDoc.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    setScrollY(window.scrollY)

    setModalIndex(index)
    setModalState(modalState)
  }
  const hideModal = () => {
    const htmlDoc = document.children[0] as HTMLElement;
    htmlDoc.style.overflow = "";
    document.body.style.overflow = "";

    setModalState(undefined)
  }

  const hasEmpty = (states: IModalState[]) => {
    let hasEmpty = false
    states.forEach((state) => {
      if (state.make === null) {
        hasEmpty = true
      }
    })

    return hasEmpty;
  }

  // Add Vehicle
  useEffect(() => {
    if (updateStates) {
      setUpdateStates(false)

      if (modalState?.variant) { // User selected new vehicle
        modalStates[modalIndex] = modalState;
      }

      if (!hasEmpty(modalStates) && modalStates.length < vehiclesLimit) {
        modalStates.push({...base_state})
      }
  
      setModalStates(modalStates)
      setUpdateVehicles(true)
    }
  }, [updateStates, modalIndex, modalState, modalStates, vehiclesLimit])

  const removeVehicle = (state: any) => {
    const newState = modalStates.filter(modalState => modalState !== state)

    if (!hasEmpty(newState)) {
      newState.push({...base_state})
    }

    setModalStates(newState)
    setUpdateVehicles(true)
  }

  const renderEVSelector = () => {
    const selectors = modalStates.map((state, index) => (
      <EVSelector 
        key={index}
        vehicle={state.variant?.value}
        onClick={() => openModal(state, index)}
        onDelete={() => removeVehicle(state)}
      />
    ))

    return selectors
  }

  return (
    <>
      <div className={`${isBoiWebsite ? styles.evsHolderBoi : styles.evsHolder} ${modalStates.length === 4 ? styles.allSelected : " "}`}>
        { processURL 
          ? <Loader className={isBoiWebsite ? styles.loaderBoi : styles.loader} />
          : renderEVSelector() 
        }
      </div>

      { modalState && 
        <EVModal
          makes={makes} hideModal={hideModal} 
          modalState={modalState} setModalState={setModalState}
          update={() => setUpdateStates(true)}
          scrollY={scrollY}
        /> 
      }
    </>
    
  )
};